import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BankAccountDetails } from '../models/payment-preferences.model';
import { PaymentPreferencesFormService } from './payment-preferences-form.service';

export interface AccountSelectionContext {
    form: UntypedFormGroup;
    retrievedAccountDetails: BankAccountDetails[];
    formService: PaymentPreferencesFormService;
}

export interface AccountSelectionResult {
    reftSelected: boolean;
    displayReftAccount: boolean;
    isEditing: boolean;
    newAccount: boolean;
    isFormReadOnly?: boolean;
    hasAccountDetails: boolean;
    bankName: string;
    showBankName: boolean;
    isSecondaryAccount?: boolean;
}

export const DEFAULT_ACCOUNT_SELECTION_RESULT: AccountSelectionResult = {
    reftSelected: false,
    displayReftAccount: false,
    isEditing: false,
    isFormReadOnly: false,
    newAccount: false,
    hasAccountDetails: false,
    bankName: '',
    showBankName: false,
    isSecondaryAccount: false
};

export abstract class AccountSelectionStrategy {
    abstract handle(context: AccountSelectionContext, value?: string): AccountSelectionResult;
}

@Injectable()
export class ReftAccountSelectionStrategy extends AccountSelectionStrategy {
    handle(context: AccountSelectionContext): AccountSelectionResult {
        return {
            reftSelected: true,
            displayReftAccount: false,
            isFormReadOnly: true,
            isEditing: false,
            newAccount: false,
            hasAccountDetails: false,
            bankName: '',
            showBankName: false
        };
    }
}

@Injectable()
export class NewAccountSelectionStrategy extends AccountSelectionStrategy {
    handle(context: AccountSelectionContext, paymentMethodValue?: string): AccountSelectionResult {
        const newAccountType = (paymentMethodValue || '').replace(/^new-/, '');

        context.formService.clearFormFields(context.form, [
            'accountName',
            'bankRoutingNumber',
            'bankAccountNumber',
            'confirmBankAccountNumber'
        ]);

        context.form.get('bankAccountType')?.setValue(newAccountType);

        return {
            reftSelected: false,
            displayReftAccount: false,
            isFormReadOnly: false,
            isEditing: true,
            newAccount: true,
            hasAccountDetails: false,
            bankName: '',
            showBankName: false
        };
    }
}

@Injectable()
export class ExistingAccountSelectionStrategy extends AccountSelectionStrategy {
    handle(context: AccountSelectionContext, publicID?: string): AccountSelectionResult {
        const selectedAccount = context.retrievedAccountDetails.find((acct) => acct.publicID === publicID);

        if (!selectedAccount) {
            return DEFAULT_ACCOUNT_SELECTION_RESULT;
        }

        context.formService.populateFormFromAccount(context.form, selectedAccount);

        const isSecondaryAccount = !selectedAccount.primaryAccount;

        return {
            reftSelected: false,
            displayReftAccount: false,
            isFormReadOnly: true,
            isEditing: false,
            newAccount: false,
            hasAccountDetails: true,
            isSecondaryAccount: isSecondaryAccount,
            bankName: selectedAccount.bankName,
            showBankName: false
        };
    }
}
